import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Mismo contenedor que dreamland-app (todo el tracking público de Dreamland
// cae en la misma propiedad de GTM).
const GTM_ID = 'GTM-KBV543QN';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Rutas internas (admin): NO cargar GTM. El resto son páginas de cliente. */
function isPublicPath(pathname: string): boolean {
  return !pathname.startsWith('/admin');
}

/**
 * Google Tag Manager (GDPR + Analytics) para las páginas públicas.
 *
 * Réplica del patrón de dreamland-app —que usa el componente oficial de Next
 * `@next/third-parties/google` scopeado en un layout anidado— adaptado a esta
 * SPA de Vite/React: carga `gtm.js` e inicializa `dataLayer` SOLO en rutas
 * públicas (todo menos `/admin/*`). Una vez cargado persiste durante la sesión,
 * que es el comportamiento normal de una SPA.
 *
 * El consentimiento GDPR se gestiona dentro del propio contenedor GTM (Consent
 * Mode / CMP), no en código, igual que en dreamland-app.
 */
export function GoogleTagManager() {
  const { pathname } = useLocation();
  const active = isPublicPath(pathname);

  useEffect(() => {
    if (!active) return;
    // Idempotente: no reinyectar el loader si ya está en la página.
    if (document.getElementById('gtm-loader')) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const s = document.createElement('script');
    s.id = 'gtm-loader';
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(s);
  }, [active]);

  if (!active) return null;

  // Google Tag Manager (noscript) — fallback sin JavaScript, tal como especificó
  // el equipo de analytics.
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
        title="Google Tag Manager"
      />
    </noscript>
  );
}
